import { usePathname } from "expo-router";
import Head from "expo-router/head";
import { useTranslation } from "react-i18next";
import { resolveWebPageMetadata } from "../../lib/web-page-metadata";

type WebMetadata = {
  title: string;
  description: string;
};

type RouteMetaContext = {
  appName: string;
  appTagline: string;
  tCommon: (key: string) => string;
  tAuth: (key: string) => string;
};

type RouteMetaResolver = {
  match: (pathname: string) => boolean;
  resolve: (context: RouteMetaContext) => WebMetadata;
};

function matchesPath(pathname: string, path: string) {
  return pathname === path;
}

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function formatTitle(pageTitle: string, appName: string) {
  return pageTitle === appName ? appName : `${pageTitle} | ${appName}`;
}

function humanizeSegment(segment: string) {
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function buildRouteMetaResolvers(): RouteMetaResolver[] {
  return [
    {
      match: (pathname) => matchesPath(pathname, "/"),
      resolve: ({ appName, appTagline, tCommon }) => ({
        title: appName,
        description: tCommon("meta.descriptions.default") || appTagline,
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/login"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("login.heading"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/register"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("register.heading"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/forgot-password"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("forgotPassword.title"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/reset-password"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("resetPassword.title"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/auth-step"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("login.heading"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/auth-bootstrap-check"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("login.bootstrapCheckTitle"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/password-bootstrap"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("login.bootstrapPasswordTitle"), appName),
        description: tCommon("meta.descriptions.auth"),
      }),
    },
    {
      match: (pathname) =>
        matchesPath(pathname, "/onboarding") ||
        matchesPrefix(pathname, "/onboarding"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.onboarding"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/intent"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.intent.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/this-is-you"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(
          `${tAuth("onboarding.thisIsYou.titleBase")} ${tAuth("onboarding.thisIsYou.titleAccent")}`,
          appName,
        ),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/company-setup"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.companyInfo.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/company-next-action"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(
          tAuth("onboarding.companyInfo.nextActionTitle"),
          appName,
        ),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/ai-input"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.cvJourney.aiInputTitle"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/cv-health"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.cvHealth.reportTitle"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/how-to-reach-you"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(
          `${tAuth("onboarding.howToReachYou.titleBase")} ${tAuth("onboarding.howToReachYou.titleAccent")}`,
          appName,
        ),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/experience"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.experience.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/education"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.education.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/who-sees-cv"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(
          `${tAuth("onboarding.whoSeesCV.titleBase")} ${tAuth("onboarding.whoSeesCV.titleAccent")}`,
          appName,
        ),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/skills"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.skills.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/summary"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.complete.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/certifications"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.certifications.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/highlights"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(
          `${tAuth("onboarding.highlights.titleBase")} ${tAuth("onboarding.highlights.titleAccent")}`,
          appName,
        ),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/complete"),
      resolve: ({ appName, tAuth, tCommon }) => ({
        title: formatTitle(tAuth("onboarding.complete.title"), appName),
        description: tCommon("meta.descriptions.onboarding"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/chat"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.chat"), appName),
        description: tCommon("meta.descriptions.chat"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/jobs"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.jobs"), appName),
        description: tCommon("meta.descriptions.jobs"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/messages"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.messages"), appName),
        description: tCommon("meta.descriptions.messages"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/cv"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.cv"), appName),
        description: tCommon("meta.descriptions.cv"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/profile"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.profile"), appName),
        description: tCommon("meta.descriptions.profile"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/company"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.company"), appName),
        description: tCommon("meta.descriptions.company"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/candidates"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.candidates"), appName),
        description: tCommon("meta.descriptions.candidates"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/analytics"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.analytics"), appName),
        description: tCommon("meta.descriptions.analytics"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/services/library"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.library"), appName),
        description: tCommon("meta.descriptions.library"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/services/shortlists"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.shortlists"), appName),
        description: tCommon("meta.descriptions.shortlists"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/services"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.services"), appName),
        description: tCommon("meta.descriptions.services"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/notifications"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("nav.notifications"), appName),
        description: tCommon("meta.descriptions.notifications"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/settings"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("settings.title"), appName),
        description: tCommon("meta.descriptions.settings"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/account-edit"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("settings.accountEdit.title"), appName),
        description: tCommon("meta.descriptions.settings"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/upgrade"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.upgrade"), appName),
        description: tCommon("meta.descriptions.upgrade"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/create-company"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.createCompany"), appName),
        description: tCommon("meta.descriptions.company"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/auto-apply"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.autoApply"), appName),
        description: tCommon("meta.descriptions.autoApply"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/subscription/return"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.subscription"), appName),
        description: tCommon("meta.descriptions.settings"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/uae-pass"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("settings.uaePass.title"), appName),
        description: tCommon("meta.descriptions.uaePass"),
      }),
    },
    {
      match: (pathname) => matchesPrefix(pathname, "/s"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("meta.titles.share"), appName),
        description: tCommon("meta.descriptions.share"),
      }),
    },
    {
      match: (pathname) => matchesPath(pathname, "/+not-found"),
      resolve: ({ appName, tCommon }) => ({
        title: formatTitle(tCommon("errors.notFound"), appName),
        description: tCommon("meta.descriptions.default"),
      }),
    },
  ];
}

const ROUTE_META_RESOLVERS = buildRouteMetaResolvers();

export function WebPageMetadata() {
  const pathname = usePathname();
  const { t: tCommon } = useTranslation("common");
  const { t: tAuth } = useTranslation("auth");

  const normalizedPath = pathname || "/";
  const context: RouteMetaContext = {
    appName: tCommon("appName"),
    appTagline: tCommon("appTagline"),
    tCommon,
    tAuth,
  };

  const routeMeta = ROUTE_META_RESOLVERS.find((resolver) =>
    resolver.match(normalizedPath),
  );

  const routeMetadata = routeMeta
    ? routeMeta.resolve(context)
    : (() => {
        const segments = normalizedPath.split("/").filter(Boolean);
        const fallbackTitle = segments.length
          ? humanizeSegment(segments[segments.length - 1]!)
          : context.appName;

        return {
          title: formatTitle(fallbackTitle, context.appName),
          description:
            tCommon("meta.descriptions.default") || context.appTagline,
        };
      })();
  const metadata = resolveWebPageMetadata({
    pathname: normalizedPath,
    routeMetadata,
    entryMetadata: {
      title: tCommon("meta.titles.appEntry"),
      description: tCommon("meta.descriptions.appEntry"),
    },
    origin:
      typeof window === "undefined"
        ? "https://app.tabbio.com"
        : window.location.origin,
  });

  return (
    <Head>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="robots" content={metadata.robots} />
      <meta name="application-name" content={tCommon("appName")} />
      <meta name="apple-mobile-web-app-title" content={tCommon("appName")} />
      {metadata.canonicalUrl ? (
        <link rel="canonical" href={metadata.canonicalUrl} />
      ) : null}
      <meta property="og:site_name" content={tCommon("appName")} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      {metadata.canonicalUrl ? (
        <meta property="og:url" content={metadata.canonicalUrl} />
      ) : null}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
    </Head>
  );
}
